/**
 * Decides what the user sees before the app: update required, sign-in,
 * offline re-authentication, MFA enrolment, app lock — then the app itself.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { ApiError } from '../lib/api/client';
import type { AppVersionPolicy, Branding, Me } from '../lib/api/types';
import { authErrorKey, isWipeRequest } from '../lib/auth';
import { applyBranding, cacheBranding, cachedBranding, withSignedInBranding } from '../lib/branding';
import { APP_VERSION } from '../lib/device';
import { isOfflineSessionValid } from '../lib/session';
import { versionStatus, type VersionStatus } from '../lib/version';
import { LoginPage } from '../features/auth/LoginPage';
import { MfaEnrolPage } from '../features/auth/MfaEnrolPage';
import { OfflineReauthPage, UpdateRequiredPage } from '../features/auth/GateScreens';
import { AppLockGate } from '../features/auth/AppLock';
import { cacheMe, cachedMe, MeProvider, removeLegacyMeCache } from './me';
import { clearSignedOut } from '../lib/auth/iap';
import { deviceOwner, heldByAnotherUser, setDeviceOwner } from '../lib/device-owner';
import { useServices } from './services';
import { wipeDevice } from './session-actions';
import { effectiveDeviceSettings, PACK_SETTINGS_META } from '../lib/sync/pack';
import { FullScreenLoader } from './FullScreenLoader';

type Gate =
  | { k: 'booting' }
  | { k: 'signed-out'; messageKey?: string }
  | { k: 'reauth-offline' }
  | { k: 'ready'; me: Me; offline: boolean };

const POLICY_CACHE = 'sss.versionPolicy';

export function useBranding(refreshTrigger?: unknown): Branding {
  const { api } = useServices();
  const [b, setB] = useState<Branding>(cachedBranding);
  useEffect(() => {
    applyBranding(b);
  }, [b]);
  useEffect(() => {
    setB(cachedBranding());
    api
      .get<Branding>('/public/branding', { anonymous: true })
      .then((fresh) => {
        cacheBranding(fresh);
        setB(fresh);
      })
      .catch(() => {});
  }, [api, refreshTrigger]);
  return b;
}

function useVersionStatus(): { status: VersionStatus; policy: AppVersionPolicy | null; recheck: () => void } {
  const { api, events } = useServices();
  const [policy, setPolicy] = useState<AppVersionPolicy | null>(() => {
    try {
      const v = localStorage.getItem(POLICY_CACHE);
      return v ? (JSON.parse(v) as AppVersionPolicy) : null;
    } catch {
      return null;
    }
  });
  const recheck = useCallback(() => {
    api
      .get<AppVersionPolicy>('/public/app-version', { anonymous: true })
      .then((p) => {
        localStorage.setItem(POLICY_CACHE, JSON.stringify(p));
        setPolicy(p);
      })
      .catch(() => {});
  }, [api]);
  useEffect(() => {
    recheck();
    const onVisible = () => document.visibilityState === 'visible' && recheck();
    document.addEventListener('visibilitychange', onVisible);
    document.addEventListener('resume', recheck);
    const off = events.subscribe((e) => e.type === 'upgrade-required' && recheck());
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      document.removeEventListener('resume', recheck);
      off();
    };
  }, [recheck, events]);
  return { status: policy ? versionStatus(APP_VERSION, policy) : 'ok', policy, recheck };
}

export interface GateRenderProps {
  versionStatus: VersionStatus;
  versionPolicy: AppVersionPolicy | null;
}

export function AuthGate({ children }: { children: (p: GateRenderProps) => ReactNode }) {
  const services = useServices();
  const { auth, api, events } = services;
  const [gate, setGate] = useState<Gate>({ k: 'booting' });
  const branding = useBranding(gate.k === 'signed-out');
  const version = useVersionStatus();
  const session = useSyncExternalStore(
    useCallback((l) => auth.subscribe(l), [auth]),
    () => auth.current()?.email ?? null,
  );
  const [redirectDone, setRedirectDone] = useState(false);

  // Load the persisted session and finish a Google redirect sign-in, if one is pending.
  useEffect(() => {
    auth
      .completeRedirect()
      .catch((e) => setGate({ k: 'signed-out', messageKey: authErrorKey(e) }))
      .finally(() => setRedirectDone(true));
  }, [auth]);

  // One account error at a time: a refused request is reported by /me and by every
  // request in flight, and each wipe drains the outbox for up to a minute.
  const accountError = useRef<Promise<void> | null>(null);
  const handleAccountError = useCallback(
    (code: string, wipe: boolean): Promise<void> => {
      if (accountError.current) return accountError.current;
      const run = (async () => {
        // A wipe never deletes work that could not be uploaded; the user is told it was kept (ADR-0021).
        const kept = wipe ? (await wipeDevice(services)).keptPending : (await auth.signOut(), 0);
        setGate({ k: 'signed-out', messageKey: kept > 0 ? 'auth.disabledDataKept' : authErrorKey(code) });
      })().finally(() => {
        accountError.current = null;
      });
      accountError.current = run;
      return run;
    },
    [auth, services],
  );

  const loadMe = useCallback(async () => {
    if (!auth.current()) {
      setGate((g) => (g.k === 'signed-out' ? g : { k: 'signed-out' }));
      return;
    }
    // The pack's per-site settings can only tighten the app lock and offline limit (B5, ADR-0023).
    const tighten = async (m: Me): Promise<Me> => ({
      ...m,
      settings: effectiveDeviceSettings(m.settings, await services.storage.getMeta<Parameters<typeof effectiveDeviceSettings>[1]>(PACK_SETTINGS_META).catch(() => null)),
    });
    try {
      const me = await api.get<Me>('/me');
      // Someone else's unsent work is waiting here: they must send it first (ADR-0021).
      const unsent = (await services.storage.all().catch(() => [])).length;
      if (heldByAnotherUser(deviceOwner(), me.user.id, unsent)) {
        await auth.signOut();
        setGate({ k: 'signed-out', messageKey: 'auth.otherUsersWork' });
        return;
      }
      setDeviceOwner(me.user);
      await cacheMe(services.storage, me);
      clearSignedOut();
      setGate({ k: 'ready', me: await tighten(me), offline: false });
    } catch (e) {
      if (e instanceof ApiError && e.isNetwork) {
        const raw = await cachedMe(services.storage);
        const cached = raw ? await tighten(raw) : null;
        if (cached && cached.user.email === auth.current()?.email && isOfflineSessionValid(cached.settings.offlineReauthDays ?? 7)) {
          setGate({ k: 'ready', me: cached, offline: true });
        } else {
          setGate({ k: 'reauth-offline' });
        }
        return;
      }
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        await handleAccountError(e.code, isWipeRequest(e));
        return;
      }
      setGate({ k: 'signed-out', messageKey: authErrorKey(e) });
    }
  }, [api, auth, handleAccountError, services]);

  useEffect(() => {
    if (redirectDone) void loadMe();
  }, [redirectDone, session, loadMe]);

  // Requests still in flight after sign-out are not news (loadMe handles a sign-in that fails).
  const signedOut = gate.k === 'signed-out';
  useEffect(
    () => events.subscribe((e) => e.type === 'account-error' && !signedOut && void handleAccountError(e.code, e.wipe)),
    [events, handleAccountError, signedOut],
  );

  // Signed in, the branding of the user's own region/country/business applies (ADR-0023); signed out, the public one.
  const signedInSettings = gate.k === 'ready' ? gate.me.settings : null;
  useEffect(() => {
    applyBranding(withSignedInBranding(branding, signedInSettings));
  }, [branding, signedInSettings]);

  if (version.status === 'update-required') return <UpdateRequiredPage policy={version.policy} />;
  if (gate.k === 'booting' || !redirectDone) return <FullScreenLoader />;
  if (gate.k === 'signed-out') return <LoginPage branding={branding} messageKey={gate.messageKey} />;
  if (gate.k === 'reauth-offline') return <OfflineReauthPage onRetry={loadMe} />;

  if (gate.me.user.needsMfaEnrolment) return <MfaEnrolPage onDone={loadMe} />;

  const content = children({ versionStatus: version.status, versionPolicy: version.policy });
  return (
    <MeProvider me={gate.me} offline={gate.offline} refresh={loadMe}>
      {gate.me.user.mfaExempt ? (
        content
      ) : (
        <AppLockGate lockAfterMinutes={gate.me.settings.appLockMinutes ?? 5}>{content}</AppLockGate>
      )}
    </MeProvider>
  );
}
